import { Button, CloseButton, Dialog, Field, Input, Portal, Stack } from '@chakra-ui/react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { t } from 'i18next';
import { useRef, useState } from 'react';
import { FaPlus } from 'react-icons/fa';

import { ApiError, CommitmentGoalCreate, CommitmentGoalsService } from '@/client';
import useCustomToast from '@/hooks/use-custom-toast';
import { handleError } from '@/utils';

export const CommitmentGoalAddDialog = ({
  slug,
  commitmentId,
  order,
}: {
  slug: string;
  commitmentId: string;
  order: number;
}) => {
  const ref = useRef<HTMLInputElement>(null);
  const [open, setOpen] = useState<boolean>(false);
  const [name, setName] = useState<string>('');
  const queryClient = useQueryClient();
  const showToast = useCustomToast();

  const mutationCommitment = useMutation({
    mutationFn: async ({ data }: { data: CommitmentGoalCreate }) =>
      await CommitmentGoalsService.createGoal({
        companySlug: slug,
        requestBody: data,
      }),
    onSuccess: async () => {
      showToast('Succès', 'Objectif ajouté avec succès', 'success');
      await queryClient.invalidateQueries({
        queryKey: ['commitments_goals', { slug, commitment_id: commitmentId }],
      });
      setOpen(false);
    },
    onError: (err: ApiError) => {
      handleError(err, showToast);
    },
  });

  const onSubmit = () => {
    mutationCommitment.mutate({
      data: {
        description: name,
        commitment_id: commitmentId,
        order,
      },
    });
  };

  return (
    <Dialog.Root lazyMount initialFocusEl={() => ref.current} open={open} onOpenChange={(e) => setOpen(e.open)}>
      <Dialog.Trigger asChild>
        <Button w="full" variant="outline" borderStyle={'dashed'} borderColor={'gray.300'}>
          Ajouter un objectif <FaPlus size={16} />
        </Button>
      </Dialog.Trigger>
      <Portal>
        <Dialog.Backdrop />
        <Dialog.Positioner>
          <Dialog.Content>
            <Dialog.Header>
              <Dialog.Title>Ajouter un objectif</Dialog.Title>
            </Dialog.Header>
            <Dialog.Body pb="4">
              <Stack gap="4">
                <Field.Root required>
                  <Field.Label>
                    Nom de l'objectif
                    <Field.RequiredIndicator />
                  </Field.Label>
                  <Input placeholder="Nom de l'objectif" value={name} onChange={(e) => setName(e.target.value)} />
                </Field.Root>
              </Stack>
            </Dialog.Body>
            <Dialog.Footer>
              <Dialog.ActionTrigger asChild>
                <Button variant="outline">Cancel</Button>
              </Dialog.ActionTrigger>
              <Button onClick={onSubmit} bg={'primary'} disabled={name === ''}>
                {t('ui.save')}
              </Button>
            </Dialog.Footer>
            <Dialog.CloseTrigger asChild>
              <CloseButton size="sm" />
            </Dialog.CloseTrigger>
          </Dialog.Content>
        </Dialog.Positioner>
      </Portal>
    </Dialog.Root>
  );
};
